import { Alert, Box, Button, Icon, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useProfile } from "../../../../context/ProfileContext";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useToast } from "../../../../shared/hooks";
import { ISetupCharge, SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupChargeTotals, TSetupChargeAction } from "../../../../shared/services/api/setupCharge/Types";
import { copyToClipboard } from "../../../../shared/utils";
import { buildSetupChargeListParams, EMPTY_SETUP_CHARGE_FILTERS, ISetupChargeFilterState } from "../../utils/setupChargeFormat";
import { ModalCreateSetupCharge } from "../Modals/ModalCreateSetupCharge";
import { ModalSetupChargeAction } from "../Modals/ModalSetupChargeAction";
import { ModalSetupChargeBatch, TSetupChargeBatchAction } from "../Modals/ModalSetupChargeBatch";
import { ModalSetupChargeDetail } from "../Modals/ModalSetupChargeDetail";
import { ModalSetupChargePartner } from "../Modals/ModalSetupChargePartner";
import { SetupChargeFilters } from "../SetupChargeFilters";
import { SetupChargeReport } from "../SetupChargeReport";
import { SetupChargeRequestsPanel } from "../SetupChargeRequestsPanel";
import { SetupChargeTotals } from "../SetupChargeTotals";
import { TableSetupCharge, TSetupChargeRowAction } from "../TableSetupCharge";

type TView = "charges" | "requests" | "report";

export const SetupChargeTab = () => {
	const toastHandler = useToast();
	const { isSuperAdmin } = useProfile();
	const canAdd = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.Add);
	const canManage = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.Update);

	const [view, setView] = useState<TView>("charges");
	const [filters, setFilters] = useState<ISetupChargeFilterState>(EMPTY_SETUP_CHARGE_FILTERS);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [totals, setTotals] = useState<ISetupChargeTotals | undefined>();
	const [rows, setRows] = useState<ISetupCharge[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isPartnerOpen, setIsPartnerOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [detailId, setDetailId] = useState<string | null>(null);
	const [detailRefresh, setDetailRefresh] = useState(0);
	const [actionState, setActionState] = useState<{ action: TSetupChargeAction; charge: ISetupCharge } | null>(null);
	const [batchAction, setBatchAction] = useState<TSetupChargeBatchAction | null>(null);

	const showToast = (type: "success" | "error", content: string) =>
		toastHandler.present({ type, position: "top-right", messageOrContent: <ToastContent title="Taxa Setup Avulsa" content={content} /> });

	const handleFetchData = useCallback(
		(currentFilters: ISetupChargeFilterState, currentPage: number, limit: number) => {
			setIsLoading(true);

			SetupChargeService.list(buildSetupChargeListParams(currentFilters, currentPage, limit))
				.then((result) => {
					if (result instanceof Error) {
						setRows([]);
						setTotalCount(0);
						setTotals(undefined);
						setErrorMessage(result.message);
						showToast("error", result.message);
						return;
					}

					setErrorMessage(null);
					setRows(result.data ?? []);
					setTotalCount(result.totalCount ?? 0);
					setTotals(result.totals);
					setSelectedIds([]);
				})
				.finally(() => setIsLoading(false));
		},
		[],
	);

	useEffect(() => {
		handleFetchData(EMPTY_SETUP_CHARGE_FILTERS, 0, rowsPerPage);
	}, []);

	const reload = () => {
		handleFetchData(filters, page, rowsPerPage);
		setDetailRefresh((current) => current + 1);
	};

	const handleApplyFilters = (next: ISetupChargeFilterState) => {
		setFilters(next);
		setPage(0);
		handleFetchData(next, 0, rowsPerPage);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		handleFetchData(filters, newPage, rowsPerPage);
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		handleFetchData(filters, 0, newRowsPerPage);
	};

	const handleRowAction = (action: TSetupChargeRowAction, row: ISetupCharge) => {
		if (action === "detail") {
			setDetailId(row.id);
			return;
		}
		if (action === "copy_link") {
			copyToClipboard(row.paymentLink ?? "").then(() => showToast("success", "Link de pagamento copiado."));
			return;
		}
		setActionState({ action, charge: row });
	};

	const handleActionDone = (action: TSetupChargeAction) => {
		const messages: Record<TSetupChargeAction, string> = {
			cancel: "Taxa cancelada.",
			resend: "Link reenviado ao pagador.",
			contact_update: "Contato do pagador corrigido.",
			manual_payment: "Baixa manual registrada.",
			repass: "Repasse registrado.",
			reprocess: "Taxa reprocessada.",
		};
		setActionState(null);
		showToast("success", messages[action]);
		reload();
	};

	const selectedRows = rows.filter((row) => selectedIds.includes(row.id));

	return (
		<Box>
			<LoadingComp isopen={isLoading} />

			<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mt={1} mr={4} ml={1}>
				<Tabs value={view} onChange={(_, next: TView) => setView(next)} aria-label="Visões da Taxa Setup Avulsa">
					<Tab value="charges" label="Taxas" />
					<Tab value="requests" label="Solicitações e fila" />
					<Tab value="report" label="Relatório" />
				</Tabs>
				<Box display="flex" gap={1}>
					{canManage && (
						<Button variant="outlined" startIcon={<Icon>webhook</Icon>} onClick={() => setIsPartnerOpen(true)}>
							Webhook do parceiro
						</Button>
					)}
					{canAdd && (
						<Button variant="contained" disableElevation startIcon={<Icon>add</Icon>} onClick={() => setIsCreateOpen(true)}>
							Nova taxa
						</Button>
					)}
				</Box>
			</Box>

			{view === "charges" && (
				<>
					<SetupChargeFilters applied={filters} onApply={handleApplyFilters} disabled={isLoading} />
					<SetupChargeTotals totals={totals} />

					{errorMessage && (
						<Box mt={1} mx={1}>
							<Alert severity="error">{errorMessage}</Alert>
						</Box>
					)}

					{canManage && selectedRows.length > 0 && (
						<Box display="flex" alignItems="center" gap={1} mt={1} mx={1}>
							<Button size="small" variant="outlined" onClick={() => setBatchAction("resend")}>
								Reenviar link ({selectedRows.length})
							</Button>
							<Button size="small" variant="outlined" onClick={() => setBatchAction("repass")}>
								Registrar repasse ({selectedRows.length})
							</Button>
							<Button size="small" variant="outlined" color="error" onClick={() => setBatchAction("cancel")}>
								Cancelar taxas ({selectedRows.length})
							</Button>
							<Button size="small" onClick={() => setSelectedIds([])}>
								Limpar seleção
							</Button>
						</Box>
					)}

					<TableSetupCharge
						rows={rows}
						page={page}
						rowsPerPage={rowsPerPage}
						totalCount={totalCount}
						isLoading={isLoading}
						canManage={canManage}
						selectedIds={selectedIds}
						onSelectionChange={setSelectedIds}
						onAction={handleRowAction}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</>
			)}

			{view === "requests" && <SetupChargeRequestsPanel canManage={canManage} onOpenCharge={setDetailId} />}

			{view === "report" && <SetupChargeReport />}

			<ModalCreateSetupCharge isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={reload} />

			<ModalSetupChargeDetail
				chargeId={detailId}
				refreshKey={detailRefresh}
				canManage={canManage}
				onClose={() => setDetailId(null)}
				onAction={(action, charge) => setActionState({ action, charge })}
			/>

			<ModalSetupChargeAction
				action={actionState?.action ?? null}
				charge={actionState?.charge ?? null}
				onClose={() => setActionState(null)}
				onDone={(action) => handleActionDone(action)}
			/>

			<ModalSetupChargeBatch action={batchAction} charges={selectedRows} onClose={() => setBatchAction(null)} onFinished={reload} />

			{isPartnerOpen && <ModalSetupChargePartner onClose={() => setIsPartnerOpen(false)} />}
		</Box>
	);
};
